// src/utils/dtrCompleteness.js
//
// Derived DTR completeness (PRD_DtrCompletenessGuard C1/C2). Completeness is NOT
// stored on a record — it is a pure function of the record's own time fields,
// computed at read time (same derive-on-read discipline as dtrLockDerivation.js;
// a stored status would drift, the bug class behind the stale payrollLock stamp).
//
// A record is:
//   - complete   if it is intentionally timeless (leave / absent, or a rest-day row
//                 with NO times) OR a synthesized placeholder (isPlaceholder /
//                 status:'pending') — checked FIRST, because leave/absent rows are
//                 persisted with a MIDNIGHT `timeIn` and null `timeOut`
//                 (AddTimeRecordModal), so the one-sided check below would otherwise
//                 mis-flag every leave day; OR it has BOTH timeIn and timeOut (an
//                 overnight/night-diff shift is one such record). A rest-day row WITH a
//                 punch is judged like any other (PRD_TimeRecordAndPayFixes C7): the
//                 isRestDay schedule snapshot is stamped on every rest-day punch.
//   - incomplete if EXACTLY ONE of timeIn/timeOut is present (a genuine one-sided
//                 orphan). A no-times, non-timeless, non-placeholder row is INERT
//                 (complete) — it contributes nothing and must not block payroll.
//
// `missingSide` ('in' | 'out' | null) drives ONLY a human label ("Missing time-out");
// block/filter logic uses `status` alone. The helper is applied to STORED records;
// callers that pass synthesized placeholder rows are covered by the placeholder clause.

/**
 * Whether a record is an intentionally-timeless row (nothing to pair).
 * @param {Object} r - DTR record.
 * @return {boolean}
 */
function isTimelessRow(r) {
  return r.isAbsent === true || r.onLeave === 'yes' ||
    (r.isRestDay === true && !r.timeIn && !r.timeOut); // C7
}

/**
 * Whether a record is a synthesized placeholder for an unpunched scheduled day
 * (a projection artifact, never a stored row an admin can resolve).
 * @param {Object} r - DTR record.
 * @return {boolean}
 */
function isPlaceholderRow(r) {
  return r.isPlaceholder === true || r.status === 'pending';
}

/**
 * Derive a record's pairing completeness (PRD_DtrCompletenessGuard C1/C2).
 * @param {Object} record - a STORED DTR record ({ timeIn, timeOut, ...flags }).
 * @return {{ status: ('complete'|'incomplete'), missingSide: ('in'|'out'|null) }}
 */
export function derivePairingStatus(record) {
  if (!record) return { status: 'complete', missingSide: null };
  // Placeholder / leave / absent rows are COMPLETE regardless of any times they carry —
  // checked FIRST because a leave/absent row is stored with a midnight `timeIn` and null
  // `timeOut`, so the one-sided branch below would otherwise mis-flag it. A rest-day row
  // is timeless only with no times at all (C7).
  if (isPlaceholderRow(record) || isTimelessRow(record)) {
    return { status: 'complete', missingSide: null };
  }
  const hasIn = Boolean(record.timeIn);
  const hasOut = Boolean(record.timeOut);
  if (hasIn && !hasOut) return { status: 'incomplete', missingSide: 'out' };
  if (!hasIn && hasOut) return { status: 'incomplete', missingSide: 'in' };
  // Both present → a complete pair. Neither present (a no-times, non-timeless, non-placeholder row)
  // → INERT/complete: it contributes nothing and does NOT block. Only a genuinely ONE-SIDED punch is
  // incomplete — flagging an empty row would over-block payroll on any legacy no-times record.
  return { status: 'complete', missingSide: null };
}

/**
 * Convenience for block/filter callers: is a record incomplete?
 * @param {Object} record - DTR record.
 * @return {boolean}
 */
export function isIncompleteRecord(record) {
  return derivePairingStatus(record).status === 'incomplete';
}
